/**
 * Environment-aware logging utility
 *
 * In production: Only errors are logged
 * In development: All log levels are available
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.log('Debug info');     // Only in dev
 *   logger.warn('Warning');       // Only in dev
 *   logger.error('Error', err);   // Always logged
 *   logger.debug('Verbose');      // Only in dev
 */

const isDev = import.meta.env.DEV;

type LogArgs = unknown[];

export const logger = {
  /**
   * General logging - development only
   */
  log: (...args: LogArgs): void => {
    if (isDev) {
      console.log(...args);
    }
  },

  /**
   * Warning messages - development only
   */
  warn: (...args: LogArgs): void => {
    if (isDev) {
      console.warn(...args);
    }
  },

  /**
   * Error messages - always logged (needed for debugging production issues)
   */
  error: (...args: LogArgs): void => {
    console.error(...args);
  },

  /**
   * Debug messages - development only, with [DEBUG] prefix
   */
  debug: (...args: LogArgs): void => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Info messages - development only
   */
  info: (...args: LogArgs): void => {
    if (isDev) {
      console.info(...args);
    }
  },

  /**
   * Group logging - development only
   */
  group: (label: string): void => {
    if (isDev) {
      console.group(label);
    }
  },

  groupEnd: (): void => {
    if (isDev) {
      console.groupEnd();
    }
  },

  /**
   * Table logging - development only
   */
  table: (data: unknown): void => {
    if (isDev) {
      console.table(data);
    }
  },

  /**
   * Time tracking - development only
   */
  time: (label: string): void => {
    if (isDev) {
      console.time(label);
    }
  },

  timeEnd: (label: string): void => {
    if (isDev) {
      console.timeEnd(label);
    }
  },
};

export default logger;
